import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomerLanguage, type CustomerSummary } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { apiErrorMessage } from '@/services/apiClient';
import { useUpdateCustomerMutation } from '../api/customers.api';
import { hasErrors, toCustomerPayload, validateCustomerForm } from '../validation';
import { CustomerFormFields } from './CustomerFormFields';
import { EMPTY_CUSTOMER_FORM, type CustomerFormErrors, type CustomerFormValues } from '../form';

interface EditCustomerDrawerProps {
  customer: CustomerSummary;
  isOpen: boolean;
  onClose: () => void;
}

function toFormValues(customer: CustomerSummary): CustomerFormValues {
  return {
    ...EMPTY_CUSTOMER_FORM,
    fullName: customer.displayName,
    mobile: customer.mobile,
    alternateMobile: customer.alternateMobile ?? '',
    email: customer.email ?? '',
    dateOfBirth: customer.dateOfBirth ?? '',
    // UNSPECIFIED is "not answered", so it maps to the empty option rather than a value.
    gender: customer.gender === 'UNSPECIFIED' ? '' : customer.gender,
    preferredLanguage: customer.preferredLanguage || CustomerLanguage.AR,
    notes: customer.notes ?? '',
  };
}

/** Full customer edit. A drawer rather than a modal because the field set is long. */
export function EditCustomerDrawer({ customer, isOpen, onClose }: EditCustomerDrawerProps) {
  const { t } = useTranslation();
  const [values, setValues] = useState<CustomerFormValues>(() => toFormValues(customer));
  const [errors, setErrors] = useState<CustomerFormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const updateCustomer = useUpdateCustomerMutation(customer.id);

  // Re-seed whenever the drawer opens, so a cancelled edit does not persist into the next one.
  useEffect(() => {
    if (isOpen) {
      setValues(toFormValues(customer));
      setErrors({});
      setSubmitError(null);
    }
  }, [customer, isOpen]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const validation = validateCustomerForm(values, t);
    setErrors(validation);
    if (hasErrors(validation)) return;

    // Gender cleared in the UI means "not answered", which the API models as UNSPECIFIED.
    const payload = { ...toCustomerPayload(values), gender: values.gender || 'UNSPECIFIED' };

    updateCustomer.mutate(payload, {
      onSuccess: onClose,
      onError: (error) => setSubmitError(apiErrorMessage(error) ?? t('customers.updateError')),
    });
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={t('customers.edit.title')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={updateCustomer.isPending}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form="edit-customer" isLoading={updateCustomer.isPending}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <form id="edit-customer" onSubmit={handleSubmit} noValidate className="space-y-4">
        {submitError ? <Alert tone="error">{submitError}</Alert> : null}

        <CustomerFormFields
          values={values}
          errors={errors}
          onChange={(field, value) => {
            setValues((current) => ({ ...current, [field]: value }));
            setErrors((current) => ({ ...current, [field]: undefined }));
          }}
          variant="full"
        />
      </form>
    </Drawer>
  );
}
